'use client';

import type { StaticImport } from 'next/dist/shared/lib/get-img-props';
// biome-ignore lint/style/noRestrictedImports: Used to create our custom Image component
import { default as NextImage, type ImageProps as NextImageProps } from 'next/image';
import { AnalyticsEvent, ContentPlacement } from '@/app_lib/types/analytics';
import { cn } from '@/app_lib/utils/utils';
import { placeholderFortuneImage } from '@/appconstants';

const getPathFromUrl = (url: string) => url.split('?')[0];

const customLoader = ({ src, width, quality }: { src: string; width: number; quality?: number }) =>
  `${getPathFromUrl(src)}?format=webp&w=${width}&q=${quality || 100}`;

// A single transparent pixel to minimize payload size
const dataBlur =
  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR4nGNgYAAAAAMAASsJTYQAAAAASUVORK5CYII=';

/**
 * Transforms an image source URL to request the WebP format.
 *
 * If the source URL already includes the 'format=webp' query parameter or is undefined,
 * the function returns the original source. Otherwise, it appends 'format=webp' as a query
 * parameter, using '&' or '?' as appropriate.
 *
 * @param src - The image source URL or StaticImport to transform.
 * @returns The transformed image source URL with 'format=webp' appended, or the original source if already in WebP format or undefined.
 */
function transformToWebp(src?: string | StaticImport) {
  if (src?.toString().includes('format=webp') || !src) {
    return src;
  }
  return `${src}${src.toString().includes('?') ? '&' : '?'}format=webp`;
}

function handleBrokenImage(event: React.SyntheticEvent<HTMLImageElement, Event>) {
  try {
    const target = event.currentTarget;

    if (target.src !== placeholderFortuneImage) {
      target.src = placeholderFortuneImage;
      target.srcset = ''; // clear to ensure only the placeholder is used
    }
  } catch (_error) {
    // TODO: Log the error
  }
}

interface ImageProps extends NextImageProps {
  className?: string;
  fullWidth?: boolean;
  fillProp?: boolean;
}

// TODO: Consider setting the default loading behavior to 'lazy' for performance optimization
// TODO: Make sure this component was made correctly
export const Image = ({
  className,
  // NOTE: Remove fillProp and fullWidth props
  fillProp = false,
  fullWidth = true,
  height = 200,
  loading = 'lazy',
  priority = false,
  src,
  width = 300,
  ...rest
}: ImageProps) => {
  return (
    <NextImage
      blurDataURL={dataBlur}
      className={cn('transition-opacity duration-300', className, AnalyticsEvent.ImpressionElement)}
      data-cy={rest['data-cy'] || ContentPlacement.ArticleImage}
      loader={customLoader}
      loading={priority ? 'eager' : loading}
      onError={handleBrokenImage}
      placeholder='blur'
      priority={priority}
      sizes={
        fullWidth
          ? '(max-width: 320px) 50vw, (max-width: 768px) 85vw, (max-width: 1024px) 50vw, (max-width: 1200px) 40vw, 33vw'
          : ''
      }
      src={transformToWebp(src) || placeholderFortuneImage}
      {...(!fillProp && { height })}
      {...(!fillProp && { width })}
      {...rest}
    />
  );
};
